import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";

import {
  useConfirmAuthenticator,
  useGetAuthenticator,
  useValidateAuthenticator,
} from "@api";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@components/base";
import { useUserStore } from "@store/user";
import { useAuth } from "@hooks/useAuth";

const schema = object({
  code: string()
    .transform((value) => value.trim().replace(/\s/g, ""))
    .length(6, t("validation.otpLength"))
    .required(t("validation.required")),
}).required();

const MfaSettings: FC = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { authenticator, isPendingGenerateAuthenticator } =
    useGetAuthenticator(user);
  const {
    confirmAuthenticator,
    isSuccessConfirmAuthenticator,
    isPendingConfirmAuthenticator,
  } = useConfirmAuthenticator(user);
  const { getUser } = useAuth();

  const {
    isPendingValidateAuthenticator,
    isSuccessValidateAuthenticator,
    validateAuthenticator,
    validateAuthenticatorError,
  } = useValidateAuthenticator(user);

  const { control, handleSubmit, setError, formState, reset } = useForm({
    defaultValues: {
      code: "",
    },
    resolver: yupResolver(schema),
    mode: "onSubmit",
  });

  const onSubmit = (values: { code: string }): void => {
    if (authenticator) {
      validateAuthenticator({ secret: authenticator.secret, otp: values.code });
    }
    reset();
  };

  const { isValid } = formState;

  const [isAuthenticatorActive, setIsAuthenticatorActive] = useState<boolean>(
    user?.mfaMethod != 0,
  );

  const isLoading = useMemo(() => {
    return (
      isPendingValidateAuthenticator ||
      isPendingGenerateAuthenticator ||
      isPendingConfirmAuthenticator
    );
  }, [
    isPendingValidateAuthenticator,
    isPendingGenerateAuthenticator,
    isPendingConfirmAuthenticator,
  ]);

  useEffect(() => {
    if (validateAuthenticatorError) {
      setError("code", {
        type: "string",
        message: validateAuthenticatorError?.message || t("mfa.codeIsNotValid"),
      });
    }
  }, [validateAuthenticatorError]);

  useEffect(() => {
    if (isSuccessValidateAuthenticator && authenticator) {
      confirmAuthenticator({
        secret: authenticator.secret,
      });
    }
  }, [isSuccessValidateAuthenticator]);

  useEffect(() => {
    if (isSuccessConfirmAuthenticator) {
      getUser();
      setIsAuthenticatorActive(true);
    }
  }, [isSuccessConfirmAuthenticator]);

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            {t("settings.setupMfa")}
          </Typography>
          {isAuthenticatorActive ? (
            <Stack spacing={2} alignItems="start">
              <Typography>{t("settings.mfaIsActive")}</Typography>
              <Button
                onClick={() => setIsAuthenticatorActive(false)}
                variant="contained"
                color="info"
              >
                {t("Re-authenticate")}
              </Button>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2} alignItems="start">
                {isLoading && <CircularProgress />}

                <Stack direction="column">
                  <Box textAlign="center">
                    <img
                      src={authenticator?.qrCode}
                      alt="QR"
                      style={{ width: 200 }}
                    />
                  </Box>
                  <Controller
                    control={control}
                    name="code"
                    render={({
                      field: { onBlur, onChange, name, value },
                      fieldState: { error },
                    }): React.ReactElement => (
                      <TextField
                        {...{ onBlur, onChange, name, value }}
                        error={Boolean(error)}
                        helperText={error?.message}
                        variant="outlined"
                        label={t("otp.code.title")}
                        type="string"
                        autoFocus
                      />
                    )}
                  />
                </Stack>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={!isValid}
                >
                  {t("common.connectAction")}
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MfaSettings;
